import { useContext, useMemo } from "react";
import { useDynamicConfig } from "@statsig/react-bindings";
import clsx from "clsx";
import { createIntl, createIntlCache, IntlContext } from "react-intl";
import { LoadingIndicator } from "@oai/platform/ui/Indicator";

import {
  FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY,
  FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY,
} from "@/components/creation-wizard/creationWizardBidding";
import { ONE_MILLION } from "@/routes/adManager/AdsManagerUtils";
import {
  BAZAAR_CURRENCY_DEFAULTS_CONFIG,
  getZeroDecimalCurrencyCodes,
  normalizeCurrencyCode,
  parseConfiguredCurrencyAmount,
  ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY,
} from "@/routes/adManager/CurrencyDefaults";

import s from "./BidStrength.module.css";
import { messages } from "./localization/BidStrength.messages";

import type { ReactNode } from "react";
import type { MessageDescriptor } from "react-intl";
import type {
  AdGroupMaxBidCapMicrosByCurrency,
  MaxBidCapMicrosByCurrency,
} from "@/components/creation-wizard/creationWizardBidding";
import type { ZeroDecimalCurrencyCodes } from "@/routes/adManager/CurrencyDefaults";

const BID_STRENGTH_THRESHOLDS_CONFIG_KEY = "thresholds";
const BID_STRENGTH_THRESHOLDS_BY_CURRENCY_CONFIG_KEY = "thresholds_by_currency";
const DEFAULT_CLICK_MAX_BID_AMOUNT_BY_CURRENCY_CONFIG_KEY =
  "default_click_max_bid_amount_by_currency";
const CLICK_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY =
  "click_ad_group_max_bid_cap_micros_by_currency";
const CONVERSION_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY =
  "conversion_ad_group_max_bid_cap_micros_by_currency";
const IMPRESSION_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY =
  "impression_ad_group_max_bid_cap_micros_by_currency";

export type BidStrengthThresholds = {
  weakDeliveryMinBidAmount: number;
  strongDeliveryMinBidAmount: number;
};
export type ClickMaxBidAmountByCurrency = Record<string, string | undefined>;

export type BidStrengthLevel = "may_not_deliver" | "weak_delivery" | "strong_delivery";

type BidStrengthState = {
  label: MessageDescriptor;
  description: MessageDescriptor;
  filledSegmentCount: 1 | 2 | 3;
  tone: "danger" | "caution" | "success";
};

const BID_STRENGTH_STATES: Record<BidStrengthLevel, BidStrengthState> = {
  may_not_deliver: {
    label: messages.mayNotDeliverLabel,
    description: messages.mayNotDeliverDescription,
    filledSegmentCount: 1,
    tone: "danger",
  },
  weak_delivery: {
    label: messages.weakDeliveryLabel,
    description: messages.weakDeliveryDescription,
    filledSegmentCount: 2,
    tone: "caution",
  },
  strong_delivery: {
    label: messages.strongDeliveryLabel,
    description: messages.strongDeliveryDescription,
    filledSegmentCount: 3,
    tone: "success",
  },
};
const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());

export function useBidStrengthThresholds(
  currency: string | null | undefined,
): BidStrengthThresholds | null {
  const currencyDefaultsConfig = useDynamicConfig(BAZAAR_CURRENCY_DEFAULTS_CONFIG);

  return useMemo(
    () =>
      getBidStrengthThresholdsForCurrency({
        currency,
        fallbackThresholds: currencyDefaultsConfig.get(BID_STRENGTH_THRESHOLDS_CONFIG_KEY, null),
        thresholdsByCurrency: currencyDefaultsConfig.get(
          BID_STRENGTH_THRESHOLDS_BY_CURRENCY_CONFIG_KEY,
          null,
        ),
      }),
    [currencyDefaultsConfig, currency],
  );
}

export function useDefaultClickMaxBidAmountsByCurrency(): ClickMaxBidAmountByCurrency {
  const currencyDefaultsConfig = useDynamicConfig(BAZAAR_CURRENCY_DEFAULTS_CONFIG);

  return useMemo(() => {
    const zeroDecimalCurrencyCodes = getZeroDecimalCurrencyCodes(
      currencyDefaultsConfig.get(ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY, null),
    );
    return getDefaultClickMaxBidAmountsByCurrency(
      currencyDefaultsConfig.get(DEFAULT_CLICK_MAX_BID_AMOUNT_BY_CURRENCY_CONFIG_KEY, null),
      FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY,
      zeroDecimalCurrencyCodes,
    );
  }, [currencyDefaultsConfig]);
}

export function useAdGroupMaxBidCapMicrosByCurrency(): AdGroupMaxBidCapMicrosByCurrency {
  const currencyDefaultsConfig = useDynamicConfig(BAZAAR_CURRENCY_DEFAULTS_CONFIG);

  return useMemo(
    () => ({
      clicks: getMaxBidCapMicrosByCurrency(
        currencyDefaultsConfig.get(CLICK_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY, null),
        FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY.clicks,
      ),
      conversions: getMaxBidCapMicrosByCurrency(
        currencyDefaultsConfig.get(CONVERSION_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY, null),
        FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY.conversions,
      ),
      impressions: getMaxBidCapMicrosByCurrency(
        currencyDefaultsConfig.get(IMPRESSION_MAX_BID_CAP_MICROS_BY_CURRENCY_CONFIG_KEY, null),
        FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY.impressions,
      ),
    }),
    [currencyDefaultsConfig],
  );
}

export function getBidStrengthThresholds(value: unknown): BidStrengthThresholds | null {
  if (typeof value !== "object" || value == null || Array.isArray(value)) {
    return null;
  }

  const rawThresholds = value as Record<string, unknown>;
  const weakDeliveryMinBidAmount = rawThresholds.weak_delivery_min_bid;
  const strongDeliveryMinBidAmount = rawThresholds.strong_delivery_min_bid;

  if (
    typeof weakDeliveryMinBidAmount !== "number" ||
    typeof strongDeliveryMinBidAmount !== "number" ||
    !Number.isFinite(weakDeliveryMinBidAmount) ||
    !Number.isFinite(strongDeliveryMinBidAmount) ||
    weakDeliveryMinBidAmount < 0 ||
    strongDeliveryMinBidAmount < weakDeliveryMinBidAmount
  ) {
    return null;
  }

  return {
    weakDeliveryMinBidAmount,
    strongDeliveryMinBidAmount,
  };
}

function getConfiguredThresholdsForCurrency({
  currency,
  thresholdsByCurrency,
}: {
  currency: string | null | undefined;
  thresholdsByCurrency: unknown;
}): unknown {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (
    !normalizedCurrency ||
    typeof thresholdsByCurrency !== "object" ||
    thresholdsByCurrency == null ||
    Array.isArray(thresholdsByCurrency)
  ) {
    return undefined;
  }

  return Object.entries(thresholdsByCurrency as Record<string, unknown>).find(
    ([configuredCurrency]) => configuredCurrency.trim().toUpperCase() === normalizedCurrency,
  )?.[1];
}

export function getBidStrengthThresholdsForCurrency({
  currency,
  thresholdsByCurrency,
  fallbackThresholds,
}: {
  currency: string | null | undefined;
  thresholdsByCurrency: unknown;
  fallbackThresholds: unknown;
}): BidStrengthThresholds | null {
  return (
    getBidStrengthThresholds(
      getConfiguredThresholdsForCurrency({
        currency,
        thresholdsByCurrency,
      }),
    ) ?? getBidStrengthThresholds(fallbackThresholds)
  );
}

export function getDefaultClickMaxBidAmountsByCurrency(
  value: unknown,
  fallback: ClickMaxBidAmountByCurrency = FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): ClickMaxBidAmountByCurrency {
  const normalizedFallback = Object.entries(fallback).reduce<ClickMaxBidAmountByCurrency>(
    (defaultsByCurrency, [currency, amount]) => {
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const parsedAmount = parseConfiguredCurrencyAmount({
        value: amount,
        currency: normalizedCurrency,
        zeroDecimalCurrencyCodes,
      });
      if (!normalizedCurrency || parsedAmount == null) {
        return defaultsByCurrency;
      }
      return {
        ...defaultsByCurrency,
        [normalizedCurrency]: parsedAmount,
      };
    },
    {},
  );

  if (typeof value !== "object" || value == null || Array.isArray(value)) {
    return normalizedFallback;
  }

  return Object.entries(value as Record<string, unknown>).reduce<ClickMaxBidAmountByCurrency>(
    (defaultsByCurrency, [currency, amount]) => {
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const parsedAmount = parseConfiguredCurrencyAmount({
        value: amount,
        currency: normalizedCurrency,
        zeroDecimalCurrencyCodes,
      });
      if (!normalizedCurrency || parsedAmount == null) {
        return defaultsByCurrency;
      }
      return {
        ...defaultsByCurrency,
        [normalizedCurrency]: parsedAmount,
      };
    },
    normalizedFallback,
  );
}

function parseConfiguredBidCapMicros(value: unknown): number | undefined {
  const amount =
    typeof value === "number" ? value
    : typeof value === "string" ? Number(value.trim())
    : NaN;
  if (!Number.isSafeInteger(amount) || amount <= 0) {
    return undefined;
  }
  return amount;
}

export function getMaxBidCapMicrosByCurrency(
  value: unknown,
  fallback: MaxBidCapMicrosByCurrency,
): MaxBidCapMicrosByCurrency {
  if (typeof value !== "object" || value == null || Array.isArray(value)) {
    return { ...fallback };
  }

  return Object.entries(value as Record<string, unknown>).reduce<MaxBidCapMicrosByCurrency>(
    (maxBidCapMicrosByCurrency, [currency, amount]) => {
      const normalizedCurrency = currency.trim().toUpperCase();
      const parsedAmount = parseConfiguredBidCapMicros(amount);
      if (!normalizedCurrency || parsedAmount == null) {
        return maxBidCapMicrosByCurrency;
      }
      return {
        ...maxBidCapMicrosByCurrency,
        [normalizedCurrency]: parsedAmount,
      };
    },
    { ...fallback },
  );
}

export function getBidStrengthLevel({
  bidAmountMicros,
  thresholds,
}: {
  bidAmountMicros: number | null;
  thresholds: BidStrengthThresholds | null;
}): BidStrengthLevel | null {
  if (bidAmountMicros == null || thresholds == null || !Number.isFinite(bidAmountMicros)) {
    return null;
  }

  const weakDeliveryMinBidMicros = Math.round(thresholds.weakDeliveryMinBidAmount * ONE_MILLION);
  const strongDeliveryMinBidMicros = Math.round(
    thresholds.strongDeliveryMinBidAmount * ONE_MILLION,
  );

  if (bidAmountMicros >= strongDeliveryMinBidMicros) {
    return "strong_delivery";
  }

  if (bidAmountMicros < weakDeliveryMinBidMicros) {
    return "may_not_deliver";
  }

  return "weak_delivery";
}

export function getOcpcBidWarningLevel({
  bidAmountMicros,
  warningThresholdMicros,
}: {
  bidAmountMicros: number | null;
  warningThresholdMicros: number | null;
}): BidStrengthLevel | null {
  if (
    bidAmountMicros == null ||
    warningThresholdMicros == null ||
    !Number.isFinite(bidAmountMicros) ||
    !Number.isSafeInteger(warningThresholdMicros) ||
    warningThresholdMicros <= 0
  ) {
    return null;
  }

  return bidAmountMicros < warningThresholdMicros ? "may_not_deliver" : "strong_delivery";
}

export function getBidStrengthPlaceholder({
  thresholds,
  fallbackPlaceholder,
  formatMicrosForInput,
}: {
  thresholds: BidStrengthThresholds | null;
  fallbackPlaceholder: string | undefined;
  formatMicrosForInput: (amountMicros: number) => string;
}): string | undefined {
  if (thresholds == null) {
    return fallbackPlaceholder;
  }

  return formatMicrosForInput(Math.round(thresholds.strongDeliveryMinBidAmount * ONE_MILLION));
}

export function BidStrengthIndicator({ level }: { level: BidStrengthLevel }) {
  const intl = useContext(IntlContext) ?? DEFAULT_INTL;
  const state = BID_STRENGTH_STATES[level];

  return (
    <div className={s.BidStrengthIndicator}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1" aria-hidden="true">
          {[1, 2, 3].map((segment) => (
            <span
              key={segment}
              className={clsx(
                s.BidStrengthSegment,
                segment <= state.filledSegmentCount ?
                  s[`BidStrengthSegment_${state.tone}`]
                : s.BidStrengthSegment_empty,
              )}
            />
          ))}
        </div>
        <div
          className={clsx("text-xs font-semibold", {
            "text-danger-soft": state.tone === "danger",
            "text-caution-outline": state.tone === "caution",
            "text-success-outline": state.tone === "success",
          })}
        >
          {intl.formatMessage(state.label)}
        </div>
      </div>
      <p className="text-secondary mt-1 text-xs">{intl.formatMessage(state.description)}</p>
    </div>
  );
}

export function BidStrengthField({
  children,
  level,
  isLoading = false,
}: {
  children: ReactNode;
  level: BidStrengthLevel | null;
  isLoading?: boolean;
}) {
  return (
    <div className={s.BidStrengthField}>
      <div
        className={clsx(
          s.BidStrengthInput,
          level == null && !isLoading && s.BidStrengthInput_fullWidth,
        )}
      >
        {children}
      </div>
      {isLoading ?
        <BidStrengthLoadingIndicator />
      : level == null ?
        null
      : <BidStrengthIndicator level={level} />}
    </div>
  );
}

function BidStrengthLoadingIndicator() {
  const intl = useContext(IntlContext) ?? DEFAULT_INTL;

  return (
    <div className={s.BidStrengthIndicator} role="status" aria-live="polite">
      <div className="text-secondary flex items-center gap-2 text-xs">
        <LoadingIndicator size={16} />
        <span>{intl.formatMessage(messages.checkingBidStrength)}</span>
      </div>
    </div>
  );
}
